import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { decodeData } from '../../../../utils/encodeAndEncode';
import { getVideoDetailsById } from '../../../../services/adminService';
import SecureVideoPlayer from '../../../../components/common/SecureVideoPlayer';
import Loader from '../../../../components/admin/Loader';

function AdminVideoPlayerView() {
  const { courseId, moduleId, contentId } = useParams();
  const decodedContentId = decodeData(contentId);
  const decodedModuleId = decodeData(moduleId);
  const playerContainerRef = useRef(null);
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideoDetails = async () => {
      try {
        setLoading(true);
        const response = await getVideoDetailsById({
          video_id: decodedContentId,
          module_id: decodedModuleId
        });
        if (response.success) {
          setVideoData(response.data);
        }
      } catch (error) {
        console.error('Error fetching video details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (decodedContentId && decodedModuleId) {
      fetchVideoDetails();
    }
  }, [decodedContentId, decodedModuleId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="video-section-wrapper px-4 md:px-8 py-5 max-w-6xl mx-auto">
      <div className="mb-5 flex items-center gap-3">
        <Link
          to={`/admin/approvalRequest/view/${courseId}/content/${moduleId}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 no-underline"
          title="Back to Module Content"
        >
          <Icon icon="fluent:arrow-left-24-regular" width={18} height={18} />
        </Link>
        <div>
          <h4 className="mb-0 text-lg font-semibold text-gray-900">
            {videoData?.video_title || videoData?.video_name || 'Video Player'}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">View only mode</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
        <div className="border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-800">Preview</span>
          {videoData?.video_duration && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-[11px] text-gray-600 border border-gray-200">
              <Icon icon="fluent:timer-24-regular" width={14} height={14} />
              {videoData.video_duration}
            </span>
          )}
        </div>
        <div className="bg-black/5">
          <SecureVideoPlayer
            ref={playerContainerRef}
            videoUrl={videoData?.video_url}
            className="admin-video-player"
            autoplay={false}
            controls={true}
            fluid={true}
            responsive={true}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium">
            <Icon icon="fluent:video-24-regular" width={14} height={14} />
            Video content
          </span>
          {videoData?.is_active !== undefined && (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                videoData.is_active
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
            >
              {videoData.is_active ? 'Active' : 'Inactive'}
            </span>
          )}
        </div>
        {videoData?.video_description && (
          <p className="text-xs sm:text-sm text-gray-600 leading-snug">
            {videoData.video_description}
          </p>
        )}
      </div>
    </div>
  );
}

export default AdminVideoPlayerView;
